import { expandExponentialString } from '../../helpers/commonHelper';

const multiplyIntegerStrings = (a, b) => {
  if (a === '0' || b === '0') return '0';
  const result = Array(a.length + b.length).fill(0);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      const mul = Number(a[i]) * Number(b[j]);
      const pos2 = i + j + 1;
      const sum = mul + result[pos2];
      result[pos2] = sum % 10;
      result[i + j] += Math.floor(sum / 10);
    }
  }
  let start = 0;
  while (start < result.length - 1 && result[start] === 0) start += 1;
  return result.slice(start).join('');
};

/** Multiply two human decimal amounts without Number precision loss. */
export const multiplyHumanDecimals = (a, b, maxDecimals = 18) => {
  let sa = String(a ?? '').trim().replace(/,/g, '');
  let sb = String(b ?? '').trim().replace(/,/g, '');
  if (/e/i.test(sa)) sa = expandExponentialString(sa);
  if (/e/i.test(sb)) sb = expandExponentialString(sb);
  if (!sa || !sb) return '0';

  const negA = sa.startsWith('-');
  const negB = sb.startsWith('-');
  const absA = negA ? sa.slice(1) : sa;
  const absB = negB ? sb.slice(1) : sb;
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(absA) || !/^(\d+(\.\d*)?|\.\d+)$/.test(absB)) {
    return '0';
  }

  const [intA = '0', fracA = ''] = absA.split('.');
  const [intB = '0', fracB = ''] = absB.split('.');
  const product = multiplyIntegerStrings(`${intA}${fracA}` || '0', `${intB}${fracB}` || '0');
  const scale = fracA.length + fracB.length;
  let result = product;

  if (scale > 0) {
    if (result.length <= scale) {
      result = `0.${result.padStart(scale, '0')}`;
    } else {
      const intPart = result.slice(0, result.length - scale) || '0';
      let fracPart = result.slice(result.length - scale);
      if (Number.isFinite(maxDecimals)) {
        fracPart = fracPart.slice(0, Math.max(0, maxDecimals));
      }
      fracPart = fracPart.replace(/0+$/, '');
      result = fracPart ? `${intPart}.${fracPart}` : intPart;
    }
  }

  result = result.replace(/^(-?)0+(?=\d)/, '$1');
  const negative = negA !== negB;
  if (negative && result !== '0') result = `-${result}`;
  return result;
};

/**
 * Normalize any numeric input to a plain decimal string (never scientific notation).
 * Safe for Number inputs that would otherwise stringify as "9.007…e+23".
 */
export const toPlainNumberString = (value) => {
  if (value == null || value === '') return '0';

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '0';
    if (value === 0) return '0';
    const plain = value.toLocaleString('en-US', {
      useGrouping: false,
      maximumFractionDigits: 20,
      minimumFractionDigits: 0,
    });
    return /e/i.test(plain) ? expandExponentialString(plain) : plain;
  }

  let raw = String(value).trim().replace(/,/g, '');
  if (!raw) return '0';
  if (/e/i.test(raw)) raw = expandExponentialString(raw);
  return raw || '0';
};

/**
 * Convert a human decimal amount to an integer token-units string.
 * Uses string math only — never Number()*10**decimals (that emits e+23 and breaks BigInt).
 */
export const decimalAmountToTokenUnits = (amount, decimals) => {
  const safeDecimals =
    Number.isFinite(Number(decimals)) && Number(decimals) >= 0
      ? Math.floor(Number(decimals))
      : 18;

  const raw = toPlainNumberString(amount);
  if (!/^[+-]?\d+(\.\d+)?$/.test(raw)) return '0';

  const negative = raw.startsWith('-');
  const abs = negative ? raw.slice(1) : raw;
  const [intRaw = '0', fracRaw = ''] = abs.split('.');
  const intPart = intRaw.replace(/^0+(?=\d)/, '') || '0';
  const fracPart = fracRaw.padEnd(safeDecimals, '0').slice(0, safeDecimals);
  const combined = `${intPart}${fracPart}`.replace(/^0+(?=\d)/, '') || '0';

  if (combined === '0') return '0';
  return negative ? `-${combined}` : combined;
};

/**
 * Force a uint256-safe decimal digit string for web3 / MetaMask BigInt encoders.
 * Never returns scientific notation; never passes a JS Number through.
 */
export const toUint256String = (value) => {
  if (typeof value === 'bigint') return value < 0n ? '0' : value.toString(10);

  const plain = toPlainNumberString(value);
  if (!/^[+-]?\d+(\.\d+)?$/.test(plain)) return '0';

  const negative = plain.startsWith('-');
  if (negative) return '0';

  const [intPart = '0'] = plain.split('.');
  return intPart.replace(/^0+(?=\d)/, '') || '0';
};
